class Navigation {
  constructor() {
    this.header = document.querySelector('.wp-header');
    this.menuBackdrop = document.querySelector('.wp-menu__backdrop');
    this.openKey = null;
    this.desktopQuery = window.matchMedia('(min-width: 1330px)');

    this.assignDropdownFallbacks();

    this.triggers = document.querySelectorAll('.wp-header__navigation-main .wp-menu-a__item--has-dropdown');
    this.dropdowns = document.querySelectorAll('.wp-header__dropdowns .wp-header__dropdown');

    if (this.triggers.length) {
      this.bindTriggers();
    }

    if (this.menuBackdrop) {
      this.bindBackdrop();
    }

    this.bindAnchorLinks();

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAllMenus();
      }
    });
  }

  assignDropdownFallbacks() {
    // Prefer Management → Sustainability → Financial among rendered panels.
    const preferred = ['management', 'sustainability', 'financial'];
    const available = new Set(
      Array.from(document.querySelectorAll('.wp-header__dropdowns .wp-header__dropdown'))
        .map((el) => el.getAttribute('data-dropdown'))
        .filter(Boolean)
    );
    const keys = preferred.filter((key) => available.has(key));
    const items = document.querySelectorAll('.wp-header__navigation-main .wp-menu-a--main > .wp-menu-a__item--level-0');

    items.forEach((item, index) => {
      if (item.getAttribute('data-dropdown') || !keys[index]) {
        return;
      }
      const key = keys[index];
      item.setAttribute('data-dropdown', key);
      item.classList.add('wp-menu-a__item--has-dropdown');
    });
  }

  bindTriggers() {
    this.triggers.forEach((item) => {
      const key = item.getAttribute('data-dropdown');
      const trigger = item.querySelector('.wp-menu-a__link--level-0');
      if (!key || !trigger) {
        return;
      }

      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', `wp-header-dropdown-${key}`);

      trigger.addEventListener('click', (e) => {
        if (!this.desktopQuery.matches) {
          // Mobile handled by NavigationResponsive
          return;
        }

        e.preventDefault();
        if (this.openKey === key) {
          this.closeMenu();
        } else {
          this.openMenu(key, item, trigger);
        }
      });
    });
  }

  bindBackdrop() {
    this.menuBackdrop.addEventListener('click', (e) => {
      e.preventDefault();
      this.closeMenu();
    });
  }

  /**
   * Main Menu Anchor Links: close megamenu, then scroll (same page) or navigate.
   */
  bindAnchorLinks() {
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a.wp-header__dropdown-primary-link');
      if (!link) {
        return;
      }

      const href = link.getAttribute('href');
      if (!href) {
        return;
      }

      const hashIndex = href.indexOf('#');
      const hash = hashIndex >= 0 ? href.slice(hashIndex) : '';
      const pathPart = hashIndex >= 0 ? href.slice(0, hashIndex) : href;
      const samePage = this.isCurrentPageUrl(pathPart) && hash.length > 1;
      const target = samePage ? this.findHashTarget(hash) : null;

      this.closeAllMenus();

      if (samePage && target) {
        e.preventDefault();
        // Wait for menu close / header height to settle
        window.requestAnimationFrame(() => {
          window.setTimeout(() => {
            this.scrollToElement(target);
            if (history.replaceState) {
              history.replaceState(null, '', hash);
            } else {
              window.location.hash = hash;
            }
          }, 50);
        });
      }
    });
  }

  isCurrentPageUrl(url) {
    if (!url || url === '' || url === '/') {
      // bare "#anchor" or home-relative — treat as same page only on front page
      return this.isFrontPage();
    }

    try {
      const resolved = new URL(url, window.location.origin);
      const current = new URL(window.location.href);
      const normalize = (pathname) => pathname.replace(/\/$/, '') || '/';
      return (
        resolved.origin === current.origin &&
        normalize(resolved.pathname) === normalize(current.pathname)
      );
    } catch (err) {
      return false;
    }
  }

  isFrontPage() {
    return document.body.classList.contains('home') || document.body.classList.contains('front-page');
  }

  findHashTarget(hash) {
    const id = decodeURIComponent(hash.replace(/^#/, ''));
    if (!id) {
      return null;
    }
    return document.getElementById(id) || document.querySelector(hash);
  }

  scrollToElement(target) {
    const offset = this.getScrollOffset();
    const top = target.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({
      top: Math.max(0, top),
      behavior: 'smooth',
    });
  }

  getScrollOffset() {
    const header = document.querySelector('.wp-header');
    const stickyNav = document.querySelector('.lc-sticky-nav');
    let offset = 0;

    if (header) {
      offset += header.getBoundingClientRect().height || 0;
    }
    if (stickyNav) {
      offset += stickyNav.getBoundingClientRect().height || 0;
    }
    if (document.documentElement.classList.contains('admin-bar')) {
      offset += window.innerWidth >= 783 ? 32 : 46;
    }

    return offset + 24;
  }

  openMenu(key, item, trigger) {
    this.closeMenu(false);

    const dropdown = document.querySelector(`.wp-header__dropdowns .wp-header__dropdown[data-dropdown="${key}"]`);
    if (!dropdown) {
      return;
    }

    dropdown.hidden = false;
    dropdown.classList.add('wp-header__dropdown--open');
    item.classList.add('wp-menu--active');
    trigger.setAttribute('aria-expanded', 'true');

    if (this.menuBackdrop) {
      this.menuBackdrop.style.display = 'block';
    }

    if (this.header) {
      this.header.classList.add('wp-header--dropdown-open');
    }

    this.openKey = key;
  }

  closeMenu(hideBackdrop = true) {
    this.dropdowns.forEach((dropdown) => {
      dropdown.hidden = true;
      dropdown.classList.remove('wp-header__dropdown--open');
    });

    document.querySelectorAll('.wp-header__navigation-main .wp-menu-a__item--has-dropdown.wp-menu--active').forEach((item) => {
      item.classList.remove('wp-menu--active');
      const trigger = item.querySelector('.wp-menu-a__link--level-0');
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      }
    });

    if (hideBackdrop && this.menuBackdrop) {
      this.menuBackdrop.style.display = 'none';
    }

    if (this.header) {
      this.header.classList.remove('wp-header--dropdown-open');
    }

    this.openKey = null;
  }

  closeAllMenus() {
    this.closeMenu(true);

    const checkbox = document.querySelector('#hamburger');
    const nav = document.querySelector('.wp-header__navigation');

    if (checkbox) {
      checkbox.checked = false;
    }

    if (nav) {
      nav.classList.remove('wp-header__navigation--active', 'wp-header__navigation--panel-open');
    }

    document.body.classList.remove('wp-header-menu-open');

    document.querySelectorAll('.wp-header__dropdown--mobile').forEach((panel) => {
      panel.hidden = true;
    });
  }
}

export default Navigation;
